import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row, Container, Button, Badge, Alert } from "reactstrap";
import ResumeCash from "./ResumCash";
import { useSelector, useDispatch } from "react-redux";
import { connect } from 'react-redux';

import { setCurrentCompany } from "../../store/companies/actions";
import moment from 'moment';
import "moment/locale/es";
import TicketsStats from "../Tickets/ticketstats";
import Ticket from "./lastTickets"
import VersionAlert from "./VersionAlert"
import StatsPreview from "./StatsPreview"
import AnprPromoCard from "../../components/Common/AnprPromoCard"

// Skeleton placeholder card
const SkeletonCard = ({ height = 70 }) => (
  <Card className="flex-grow-1 mb-3">
    <CardBody className="py-2 px-3 placeholder-glow">
      <span className="placeholder col-4 mb-2" style={{ height: 10 }}></span>
      <span className="placeholder col-7" style={{ height: 16 }}></span>
      <span className="placeholder col-5 mt-1" style={{ height: 10 }}></span>
    </CardBody>
  </Card>
);

// Skeleton for stats row
const SkeletonStats = () => (
  <Card className="mb-3">
    <CardBody className="py-2 placeholder-glow">
      <Row>
        {[1, 2, 3, 4, 5].map((i) => (
          <Col key={i} sm={6} xs={6} lg={3} xl={2} className="text-center py-2">
            <span className="placeholder col-8 mb-1" style={{ height: 10 }}></span>
            <span className="placeholder col-5" style={{ height: 20 }}></span>
          </Col>
        ))}
      </Row>
    </CardBody>
  </Card>
);

// Skeleton for cash resume
const SkeletonCash = () => (
  <Row>
    {[1, 2, 3].map((i) => (
      <Col key={i} xs={12} sm={6} md={4} className="d-flex">
        <Card className="flex-grow-1 mb-3">
          <CardBody className="p-2 placeholder-glow">
            <div className="d-flex align-items-center">
              <span className="placeholder rounded-circle me-3" style={{ width: 50, height: 50 }}></span>
              <div className="flex-grow-1">
                <span className="placeholder col-6 mb-1" style={{ height: 10 }}></span>
                <span className="placeholder col-8" style={{ height: 18 }}></span>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    ))}
  </Row>
);

const Dashboard = (props) => {

  document.title = "Inicio | TicketControl - Monitor";
  const dispatch = useDispatch();
  const [lastLogin, setLastLogin] = useState(null);
  const [hasTicket, setHasTicket] = useState(false);

  useEffect(() => {
    setLastLogin(props.logins[0]);
  }, [props.logins]);

  useEffect(() => {
    setHasTicket(props.openedTickets.length > 0);
  }, [props.openedTickets]);

  const { companies, company } = useSelector((state) => ({
    companies: state.companies.companies,
    company: state.companies.currentCompany,
  }));

  const ticketsStats = useSelector((state) => state.data.ticketsStats);
  const versionDB = useSelector((state) => state.data.versionDB);
  const appConfig = useSelector((state) => state.data.appConfig) || {};
  const ipAlert = appConfig.ipMonitorAlert || null;

  const handleChangeCompany = (c) => {
    dispatch(setCurrentCompany(c));
  };

  // Data is still loading if we don't have stats yet
  const isLoading = !ticketsStats;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Row>
            <Col lg={12}>

              {/* Parking switcher - only when multiple companies */}
              {companies && companies.length > 1 && (
                <div className="mb-3 d-flex flex-wrap gap-2">
                  {companies.map((c) => (
                    <Button
                      key={c.id}
                      color={c.id === company.id ? "primary" : "light"}
                      size="sm"
                      className={c.id === company.id ? "shadow-sm" : ""}
                      onClick={() => handleChangeCompany(c)}
                    >
                      <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>local_parking</span>
                      {c.name}
                      {c.id === company.id && (
                        <Badge color="light" className="ms-2 text-primary" style={{ fontSize: 10 }}>
                          <span className="material-symbols-rounded" style={{ fontSize: 12 }}>check</span>
                        </Badge>
                      )}
                    </Button>
                  ))}
                </div>
              )}

              {/* IP Alert */}
              {ipAlert && ipAlert.expected && (
                <Alert color="danger" className="d-flex align-items-center gap-2 py-2" style={{ fontSize: 13 }}>
                  <span className="material-symbols-rounded" style={{ fontSize: 20 }}>error</span>
                  <div>
                    <strong>Alerta de red:</strong> La IP de la PC cambió de <strong>{ipAlert.expected}</strong> a <strong>{ipAlert.current}</strong>. Las cámaras y postes podrían haberse desconectado.
                    {ipAlert.timestamp && (
                      <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                        {new Date(ipAlert.timestamp).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                </Alert>
              )}

              <Row>
                {/* Company name + address */}
                <Col xxl={5} xl={5} lg={5} md={5} xs={12} sm={12} className="d-flex">
                  <Card className='flex-grow-1 mb-3'>
                    <CardBody>
                      <h2 className='text-primary mb-1'><strong>{company.name}</strong></h2>
                      <span className="text-muted">{company.address}</span>
                      {versionDB && versionDB !== "0.0.0" && (
                        <div className="mt-1" style={{ fontSize: 11, color: "#aaa" }}>
                          v{versionDB}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Last ticket - or skeleton */}
                {isLoading ? (
                  <Col xxl={true} xl={true} lg={true} md={4} xs={6} sm={6} className="d-flex">
                    <SkeletonCard />
                  </Col>
                ) : hasTicket && (
                  <Col xxl={true} xl={true} lg={true} md={4} xs={6} sm={6} className="d-flex">
                    <Ticket />
                  </Col>
                )}

                {/* Cashier - or skeleton */}
                {isLoading ? (
                  <Col xxl={true} xl={true} lg={true} md={4} xs={6} sm={6} className="d-flex">
                    <SkeletonCard />
                  </Col>
                ) : lastLogin && (
                  <Col xxl={true} xl={true} lg={true} md={4} xs={6} sm={6} className="d-flex">
                    <Card className='flex-grow-1 mb-3'>
                      <CardBody className="py-2 px-3">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>person</span>Cajero actual
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}>{lastLogin.fullName}</div>
                        <small className="text-muted">
                          Desde {moment(lastLogin.created, 'YYYY-MM-DD HH:mm').format('DD/MM HH:mm')}
                        </small>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>

              {/* Stats - or skeleton */}
              <Row>
                <Col xs={12}>
                  {isLoading ? <SkeletonStats /> : <TicketsStats />}
                </Col>

                {/* Cash resume - or skeleton */}
                <Col xs={12}>
                  {isLoading ? (
                    <SkeletonCash />
                  ) : props.resume_cash_items && props.resume_cash_items.length > 0 && (
                    <ResumeCash data={props.resume_cash_items[props.resume_cash_items.length - 1]} />
                  )}
                </Col>
              </Row>

              {/* Stats preview — se auto-oculta (return null) si no hay datos */}
              {!isLoading && <StatsPreview />}

              {/* Version update alert */}
              <div className="mt-3">
                <VersionAlert />
              </div>

              {/* Promo cámaras IA — solo a clientes sin módulo ANPR */}
              {!isLoading && <AnprPromoCard />}

            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};


const mapStateToProps = (state) => {
  return {
    customer_id: state.data.customer_id,
    currentCompany: state.companies.currentCompany,
    logins: state.data.logins,
    resume_cash_items: state.cash.resume_cash_items,
    openedTickets: state.data.openedTickets,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
